import React, { useRef } from 'react';
import { bool, func, number, string } from 'prop-types';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import useFocusReturn from '../../util/hooks/useFocusReturn';
import useIsMounted from '../../util/useIsMounted';
import config from '../../config';
import { Avatar, ExternalLink, Icon, Logo, Modal, NamedLink } from '../../components';
import { accountMenuRows } from '../Topbar/accountMenu';
import {
  authPageInfo,
  IdentityRow,
  MessagesLink,
  RoleSwitchLink,
} from '../TopbarDesktop/TopbarDesktop';

import css from './TopbarMobileMenu.module.css';

const AUDIENCE_LAB_URL = 'https://audiencelab.attractr.live';

// A guest's sheet: the bar's links (desktop shows them in the bar), or the community-only list
const guestRows = isBetaMode =>
  isBetaMode
    ? [
        { key: 'audienceLab', labelId: 'TopbarDesktop.audienceLabLink', href: AUDIENCE_LAB_URL },
        { key: 'privacy', labelId: 'TopbarDesktop.privacyPolicyLink', name: 'PrivacyPolicyPage' },
        { key: 'terms', labelId: 'TopbarDesktop.termsOfServiceLink', name: 'TermsOfServicePage' },
        {
          key: 'guidelines',
          labelId: 'TopbarDesktop.communityGuidelinesLink',
          name: 'CommunityGuidelinesPage',
        },
      ]
    : [
        { key: 'about', labelId: 'TopbarDesktop.about', name: 'AboutPage' },
        { key: 'what', labelId: 'TopbarDesktop.whatIsAttractr', name: 'AudiencePage' },
        { key: 'creators', labelId: 'TopbarDesktop.forCreators', name: 'CreatorsPage' },
        { key: 'help', labelId: 'TopbarDesktop.helpLink', name: 'HelpPage' },
      ];

const SheetRow = ({ row, currentUser, onLogout }) => {
  const label = <FormattedMessage id={row.labelId} />;
  const classes = classNames(css.row, row.tone ? css[row.tone] : null);
  if (row.kind === 'caption') {
    return <li className={css.caption}>{label}</li>;
  }
  if (row.kind === 'separator') {
    return <li className={css.separator} aria-hidden="true" />;
  }
  if (row.kind === 'identity') {
    // Slice A: the acting-as rows and the disclaimer go here (LandingMobileMenu board).
    return (
      <li>
        <IdentityRow
          className={css.identity}
          avatarClassName={css.identityAvatar}
          currentUser={currentUser}
        />
      </li>
    );
  }
  return (
    <li>
      {row.kind === 'logout' ? (
        <button type="button" className={classes} onClick={onLogout}>
          {label}
        </button>
      ) : row.href ? (
        <ExternalLink className={classes} href={row.href}>
          {label}
        </ExternalLink>
      ) : (
        <NamedLink className={classes} {...(row.linkProps || { name: row.name })}>
          {row.icon && row.tone === 'add' ? (
            <Icon name={row.icon} size={16} className={css.addIcon} />
          ) : null}
          {label}
        </NamedLink>
      )}
    </li>
  );
};

/**
 * The public top bar on phones (60px, LandingMobile boards) and its menu, a bottom sheet with a
 * grab handle (LandingMobileMenu board). Guests get Log in, Sign up and the hamburger; members the
 * messages button and their avatar, which opens their menu. The sheet is a `Modal` in the portal:
 * Escape closes it, the focus moves into it and goes back to the button that opened it.
 */
const TopbarMobileMenu = props => {
  const {
    rootClassName,
    className,
    isAuthenticated,
    isBetaMode,
    currentUser,
    unreadCount,
    isOpen,
    onOpen,
    onClose,
    onLogout,
    onManageDisableScrolling,
  } = props;
  const intl = useIntl();
  const location = useLocation();
  const mounted = useIsMounted();
  const openerRef = useRef(null);
  useFocusReturn(isOpen, openerRef);

  const isMember = mounted && isAuthenticated;
  const userRole = currentUser?.attributes?.profile?.publicData?.role;
  const { isAuthPage, isAudienceSignup } = authPageInfo(location.pathname);
  const showRoleSwitch = !isBetaMode && isAuthPage && !isAuthenticated;
  const menuLabel = intl.formatMessage({ id: 'Topbar.menuIcon' });

  const rows = isMember
    ? accountMenuRows({ userRole, isBetaMode, layout: 'sheet' })
    : guestRows(isBetaMode).map(r => ({ ...r, kind: r.href ? 'external' : 'link' }));

  const memberControls = (
    <>
      {isBetaMode ? null : <MessagesLink className={css.messages} unreadCount={unreadCount} />}
      <button
        type="button"
        ref={openerRef}
        className={classNames(css.avatarButton, { [css.avatarButtonOpen]: isOpen })}
        onClick={onOpen}
        aria-label={menuLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </button>
    </>
  );

  const guestControls = (
    <>
      {showRoleSwitch ? (
        <RoleSwitchLink className={css.login} isAudienceSignup={isAudienceSignup} />
      ) : (
        <>
          <NamedLink name="LoginPage" params={{ role: 'audience' }} className={css.login}>
            <FormattedMessage id="TopbarDesktop.login" />
          </NamedLink>
          <NamedLink
            name={isBetaMode ? 'CommunitySignupPage' : 'SignupPage'}
            params={isBetaMode ? undefined : { role: config.userRoles.audience }}
            className={css.signup}
          >
            <FormattedMessage id="TopbarDesktop.signup" />
          </NamedLink>
        </>
      )}
      <button
        type="button"
        ref={openerRef}
        className={css.menuButton}
        onClick={onOpen}
        aria-label={menuLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Icon name="menu" size={24} />
      </button>
    </>
  );

  return (
    <div className={classNames(rootClassName || css.root, className)} data-testid="topbar-phone">
      <NamedLink
        className={css.home}
        name={isBetaMode ? 'CommunityPage' : 'LandingPage'}
        title={intl.formatMessage({ id: 'Topbar.logoIcon' })}
      >
        <Logo className={css.logo} format="mobile" />
      </NamedLink>
      <div className={css.controls}>{isMember ? memberControls : guestControls}</div>
      <Modal
        id="TopbarMobileMenu"
        ariaLabel={menuLabel}
        className={css.sheetModal}
        scrollLayerClassName={css.scrim}
        containerClassName={css.sheetContainer}
        contentClassName={css.sheetContent}
        isOpen={isOpen}
        onClose={onClose}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {/* Tapping the dimmed page above the sheet closes it */}
        <button
          type="button"
          className={css.outside}
          onClick={onClose}
          tabIndex={-1}
          aria-hidden="true"
        />
        <nav className={css.sheet} aria-label={menuLabel} data-testid="topbar-sheet">
          <button
            type="button"
            className={css.handle}
            onClick={onClose}
            aria-label={intl.formatMessage({ id: 'Modal.closeModal' })}
          >
            <span className={css.handleBar} />
          </button>
          <ul className={css.list}>
            {rows.map(row => (
              <SheetRow key={row.key} row={row} currentUser={currentUser} onLogout={onLogout} />
            ))}
          </ul>
        </nav>
      </Modal>
    </div>
  );
};

TopbarMobileMenu.defaultProps = {
  rootClassName: null,
  className: null,
  isBetaMode: false,
  currentUser: null,
  unreadCount: 0,
  isOpen: false,
};

TopbarMobileMenu.propTypes = {
  rootClassName: string,
  className: string,
  isAuthenticated: bool.isRequired,
  isBetaMode: bool,
  currentUser: propTypes.currentUser,
  unreadCount: number,
  isOpen: bool,
  onOpen: func.isRequired,
  onClose: func.isRequired,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
};

export default TopbarMobileMenu;
